import { forwardRef, useEffect, useImperativeHandle } from "react"
import { Trans, useTranslation } from "react-i18next"
import {
  TextInput,
  Button,
  Group,
  Select,
  NumberInput,
  Stack,
  ThemeIcon,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import type { Contact, ContactFormValues } from "../models/contact"
import { useCreateContact, useUpdateContact, useContactTypes } from "../hooks/useContacts"
import { FormSection } from "../../../components/crud"

interface Props {
  contact?: Contact
  onSuccess: () => void
}

const ContactFormContent = forwardRef<{ submit: () => void }, Props>(({ contact, onSuccess }, ref) => {
  const { t } = useTranslation()
  const isEdit = !!contact
  const { data: typesData } = useContactTypes()
  const createMutation = useCreateContact()
  const updateMutation = useUpdateContact()

  const form = useForm<ContactFormValues>({
    initialValues: {
      type: 0,
      name: "",
      nif: null,
      address: null,
      postal_code: null,
      phone_number: null,
      mobile_number: null,
      contact_person: null,
      locale: null,
      email: null,
      email_alternative: null,
      website: null,
    },
    validate: {
      name: (v) => (v?.length ? null : t("contacts.validation.nameRequired")),
    },
  })

  useEffect(() => {
    if (contact) {
      form.setValues({
        type: contact.type,
        name: contact.name,
        nif: contact.nif,
        address: contact.address,
        postal_code: contact.postal_code,
        phone_number: contact.phone_number,
        mobile_number: contact.mobile_number,
        contact_person: contact.contact_person,
        locale: contact.locale,
        email: contact.email,
        email_alternative: contact.email_alternative,
        website: contact.website,
      })
    }
  }, [contact])

  const handleSubmit = (values: ContactFormValues) => {
    if (isEdit) {
      updateMutation.mutate(
        { id: contact.id, values },
        {
          onSuccess: () => {
            notifications.show({ color: "green", title: t("common.success"), message: t("contacts.updateSuccess") })
            onSuccess()
          },
          onError: (err) => {
            notifications.show({ color: "red", title: t("common.error"), message: err.message })
          },
        },
      )
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          notifications.show({ color: "green", title: t("common.success"), message: t("contacts.createSuccess") })
          onSuccess()
        },
        onError: (err) => {
          notifications.show({ color: "red", title: t("common.error"), message: err.message })
        },
      })
    }
  }

  const typeOptions = (typesData?.contact_types ?? []).map((t: { id: number; option: string }) => ({
    value: String(t.id),
    label: t.option,
  }))

  const isPending = createMutation.isPending || updateMutation.isPending
  const handleFormSubmit = form.onSubmit(handleSubmit)
  useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }))

  return (
    <form onSubmit={handleFormSubmit}>
      <Stack>
        <FormSection
          title={<Group gap="xs"><ThemeIcon variant="light" color="green" size="sm" radius="xl"><i className="fa fa-address-book-o" style={{ fontSize: 12 }} /></ThemeIcon>{t("contacts.contactInfo")}</Group>}
          accentColor="green"
        >
          <Group grow>
            <TextInput
              label={t("contacts.email")}
              placeholder={t("contacts.email")}
              {...form.getInputProps("email")}
            />
            <TextInput
              label={t("contacts.phone")}
              placeholder={t("contacts.phone")}
              {...form.getInputProps("phone_number")}
            />
          </Group>
          <Group grow mt="md">
            <NumberInput
              label={t("contacts.mobile")}
              placeholder={t("contacts.mobile")}
              hideControls
              {...form.getInputProps("mobile_number")}
            />
            <TextInput
              label={t("contacts.contactPerson")}
              placeholder={t("contacts.contactPerson")}
              {...form.getInputProps("contact_person")}
            />
          </Group>
          <TextInput
            label={t("contacts.emailAlternative")}
            placeholder={t("contacts.emailAlternative")}
            mt="md"
            {...form.getInputProps("email_alternative")}
          />
        </FormSection>

        <FormSection
          title={<Group gap="xs"><ThemeIcon variant="light" color="violet" size="sm" radius="xl"><i className="fa fa-map-marker" style={{ fontSize: 12 }} /></ThemeIcon>{t("contacts.addressInfo")}</Group>}
          accentColor="violet"
        >
          <TextInput
            label={t("contacts.address")}
            placeholder={t("contacts.addressPlaceholder")}
            {...form.getInputProps("address")}
          />
          <Group grow mt="md">
            <TextInput
              label={t("contacts.postalCode")}
              placeholder={t("contacts.postalCode")}
              {...form.getInputProps("postal_code")}
            />
            <TextInput
              label={t("contacts.locale")}
              placeholder={t("contacts.locale")}
              {...form.getInputProps("locale")}
            />
          </Group>
        </FormSection>

        <FormSection
          title={<Group gap="xs"><ThemeIcon variant="light" color="orange" size="sm" radius="xl"><i className="fa fa-building-o" style={{ fontSize: 12 }} /></ThemeIcon>{t("contacts.companyInfo")}</Group>}
          accentColor="orange"
        >
          <Group grow>
            <Select
              label={t("contacts.type")}
              placeholder={t("contacts.selectType")}
              data={typeOptions}
              searchable
              value={form.values.type ? String(form.values.type) : null}
              onChange={(val) => form.setFieldValue("type", val ? Number(val) : 0)}
            />
            <TextInput
              label={t("contacts.nif")}
              placeholder={t("contacts.nif")}
              {...form.getInputProps("nif")}
            />
          </Group>
          <TextInput
            label={t("contacts.name")}
            placeholder={t("contacts.namePlaceholder")}
            mt="md"
            {...form.getInputProps("name")}
          />
          <TextInput
            label={t("contacts.website")}
            placeholder={t("contacts.website")}
            mt="md"
            {...form.getInputProps("website")}
          />
        </FormSection>

      </Stack>
    </form>
  )
})

export default ContactFormContent
